import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
// import { api, ApiClientError } from "@/lib/api"; // Uncomment when backend is ready

// Define the ISP type
interface ISP {
  id: string;
  isp_name: string;
  ncii_instances: number;
  type: string;
  region: string;
  active_urls: number;
  removed_urls: number;
  takedown_rate: number; // Percentage as decimal (e.g., 0.85 for 85%)
  avg_response_time: number; // In days
  contacts: string[]; // Array of email addresses
}

const PAGE_SIZE = 50;

export function ISPList() {
  const { logout } = useAuth();
  const [isps, setIsps] = useState<ISP[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadISPs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call
      // const data = await api.isps.list(PAGE_SIZE, currentPage);
      // setIsps(data);
      // setHasMore(data.length === PAGE_SIZE);
      // setTotalCount(data.totalCount); // Assuming API returns total count

      // Mock data for now
      const mockISPs: ISP[] = [
        {
          id: "1",
          isp_name: "Comcast",
          ncii_instances: 678,
          type: "Cable/Fiber",
          region: "North America",
          active_urls: 126,
          removed_urls: 552,
          takedown_rate: 0.81,
          avg_response_time: 3.8,
          contacts: ["abuse@comcast.net", "legal@comcast.com"],
        },
        {
          id: "2",
          isp_name: "AT&T",
          ncii_instances: 734,
          type: "Fiber/DSL",
          region: "North America",
          active_urls: 137,
          removed_urls: 597,
          takedown_rate: 0.81,
          avg_response_time: 3.5,
          contacts: ["abuse@att.net", "legal@att.com"],
        },
        {
          id: "3",
          isp_name: "Verizon",
          ncii_instances: 589,
          type: "Fiber/DSL",
          region: "North America",
          active_urls: 110,
          removed_urls: 479,
          takedown_rate: 0.81,
          avg_response_time: 3.3,
          contacts: ["abuse@verizon.net"],
        },
        {
          id: "4",
          isp_name: "Charter Spectrum",
          ncii_instances: 456,
          type: "Cable",
          region: "North America",
          active_urls: 85,
          removed_urls: 371,
          takedown_rate: 0.81,
          avg_response_time: 3.6,
          contacts: ["abuse@charter.net", "legal@charter.com"],
        },
        {
          id: "5",
          isp_name: "BT Group",
          ncii_instances: 412,
          type: "Fiber/DSL",
          region: "Europe",
          active_urls: 77,
          removed_urls: 335,
          takedown_rate: 0.81,
          avg_response_time: 3.2,
          contacts: ["abuse@bt.com"],
        },
        {
          id: "6",
          isp_name: "Deutsche Telekom",
          ncii_instances: 523,
          type: "Fiber/DSL",
          region: "Europe",
          active_urls: 97,
          removed_urls: 426,
          takedown_rate: 0.81,
          avg_response_time: 3.4,
          contacts: ["abuse@telekom.de", "legal@telekom.de"],
        },
        {
          id: "7",
          isp_name: "Orange",
          ncii_instances: 389,
          type: "Fiber/DSL",
          region: "Europe",
          active_urls: 72,
          removed_urls: 317,
          takedown_rate: 0.81,
          avg_response_time: 3.1,
          contacts: ["abuse@orange.com"],
        },
        {
          id: "8",
          isp_name: "Vodafone",
          ncii_instances: 467,
          type: "Mobile/Fiber",
          region: "Europe",
          active_urls: 87,
          removed_urls: 380,
          takedown_rate: 0.81,
          avg_response_time: 3.3,
          contacts: ["abuse@vodafone.com", "compliance@vodafone.com"],
        },
        {
          id: "9",
          isp_name: "China Telecom",
          ncii_instances: 892,
          type: "Fiber/DSL",
          region: "Asia",
          active_urls: 166,
          removed_urls: 726,
          takedown_rate: 0.81,
          avg_response_time: 4.5,
          contacts: ["abuse@chinatelecom.cn"],
        },
        {
          id: "10",
          isp_name: "NTT Communications",
          ncii_instances: 345,
          type: "Fiber",
          region: "Asia",
          active_urls: 64,
          removed_urls: 281,
          takedown_rate: 0.81,
          avg_response_time: 3.0,
          contacts: ["abuse@ntt.com", "legal@ntt.com"],
        },
      ];
      setIsps(mockISPs);
      setHasMore(false);
      setTotalCount(mockISPs.length); // For mock data, total equals current data length
    } catch (err) {
      // if (err instanceof ApiClientError) {
      //   if (err.status === 401) {
      //     logout();
      //   } else {
      //     setError(err.detail || "Failed to load ISPs");
      //   }
      // } else {
      //   setError("Failed to load ISPs");
      // }
      setError("Failed to load ISPs");
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadISPs();
  }, [loadISPs]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the ISPs table
  const columns: ColumnDef<ISP>[] = [
    {
      key: "isp_name",
      header: "ISP Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.isp_name}
        </div>
      ),
      sortValue: (item) => item.isp_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region}</div>
      ),
      sortValue: (item) => item.region,
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time.toFixed(1)} days
        </div>
      ),
      sortValue: (item) => item.avg_response_time,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts.join(", ")}
        </div>
      ),
      sortValue: (item) => item.contacts.join(", "),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to ISP details or open modal
            console.log("View ISP:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];

  // Handle new ISP creation
  const handleNewISP = () => {
    // TODO: Implement new ISP modal or navigation
    console.log("Add new ISP");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export ISPs");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewISP}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add ISP
      </Button>
    </>
  );

  return (
    <DataTable
      title="Internet Service Providers"
      subtitle="Track ISPs and network enforcement"
      columns={columns}
      data={isps}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search ISPs..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="ISPs"
    />
  );
}



